import { Home, Trees, Sparkles, Mountain } from "lucide-react";

// Mapping from preset → median sqft used in pricing
const YARD_SIZES = [
  { key: "small",  label: "Small Yard",       range: "0–3,000 sq ft",      midSqft: 2000,  Icon: Home,     desc: "Townhome / starter" },
  { key: "medium", label: "Medium Yard",      range: "3,000–6,000 sq ft",  midSqft: 4500,  Icon: Trees,    desc: "Typical Jacksonville lot" },
  { key: "large",  label: "Large Yard",       range: "6,000–10,000 sq ft", midSqft: 8000,  Icon: Sparkles, desc: "Bigger family home" },
  { key: "xl",     label: "Extra Large Yard", range: "10,000+ sq ft",      midSqft: 14000, Icon: Mountain, desc: "Estate / corner lot" },
];

/**
 * YardSizeSelector — 4-button visual yard-size picker used when parcel data
 * is unavailable. Calls `onSelect(sqft)` with a representative sqft value.
 */
export default function YardSizeSelector({ selectedSqft, onSelect }) {
  const selectedKey = YARD_SIZES.reduce((acc, y) => (
    selectedSqft && Math.abs(selectedSqft - y.midSqft) < Math.abs(selectedSqft - (acc?.midSqft || 0)) ? y : acc
  ), null)?.key;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5" data-testid="yard-size-selector">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#047857] font-extrabold mb-1">
        Pick your yard size
      </div>
      <p className="text-xs text-gray-600 mb-4 leading-snug">
        We couldn't auto-detect your lot — tap the closest match. Your price will update instantly.
      </p>
      <div className="grid grid-cols-2 gap-2.5">
        {YARD_SIZES.map(({ key, label, range, midSqft, Icon, desc }) => {
          const active = selectedKey === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelect(midSqft)}
              className={[
                "text-left p-3 rounded-xl border transition-all active:scale-[0.98]",
                active
                  ? "bg-[#062818] border-[#062818] text-white shadow-[0_8px_20px_rgba(6,40,24,0.25)]"
                  : "bg-white border-gray-200 hover:border-[#10B981] text-gray-900",
              ].join(" ")}
              data-testid={`yard-size-${key}`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${active ? "bg-white/15 text-emerald-200" : "bg-emerald-50 text-[#047857]"}`}>
                  <Icon className="w-4 h-4" />
                </div>
                {active && (
                  <span className="ml-auto text-[10px] uppercase tracking-[0.14em] font-extrabold text-emerald-300">Selected</span>
                )}
              </div>
              <div className="mt-2 font-extrabold text-sm leading-tight">{label}</div>
              <div className={`text-[11px] mt-0.5 ${active ? "text-emerald-200/90" : "text-gray-500"}`}>{range}</div>
              <div className={`text-[10px] mt-1 ${active ? "text-emerald-100/70" : "text-gray-400"}`}>{desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
